import { useState, useEffect, useMemo } from 'react'
import { ArrowRightLeft, FileSearch, Download, Settings2, Info, Copy, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from "@/components/ui/button"
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"
import './index.css'

const TARGET_DB_OPTIONS = [
  "UniProtKB-AC", "UniProtKB-ID", "GeneID (EntrezGene)", "RefSeq", "GI", "PDB", "GO",
  "UniRef100", "UniRef90", "UniRef50", "UniParc", "PIR", "NCBI-taxon", "MIM",
  "UniGene", "PubMed", "EMBL", "EMBL-CDS", "Ensembl", "Ensembl_TRS", "Ensembl_PRO",
  "Additional PubMed"
];

function App() {
  const [inputText, setInputText] = useState('');
  const [detectedParams, setDetectedParams] = useState(null);
  const [isDetecting, setIsDetecting] = useState(false);
  const [isMapping, setIsMapping] = useState(false);

  // Mapping options
  const [targetDb, setTargetDb] = useState(TARGET_DB_OPTIONS[0]);
  const [fillNa, setFillNa] = useState('N/A');
  const [outputSeparator, setOutputSeparator] = useState('tab');

  // Manual overrides
  const [manualSourceDb, setManualSourceDb] = useState("");
  const [manualSeparator, setManualSeparator] = useState("");
  const [manualSpecies, setManualSpecies] = useState("");
  const [availableSpecies, setAvailableSpecies] = useState([]);

  const [mappingResults, setMappingResults] = useState(null);
  const [viewMode, setViewMode] = useState('grouped');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 50;

  useEffect(() => {
    // Fetch configuration on mount
    fetch('/api/config')
      .then(res => res.json())
      .then(data => {
        if (data.available_species) {
          setAvailableSpecies(data.available_species);
        }
      })
      .catch(err => console.error("Error fetching config:", err));
  }, []);

  // Auto-detect when input changes (debounce)
  useEffect(() => {
    const handler = setTimeout(() => {
      if (inputText.trim().length > 0) {
        performDetection(inputText);
      } else {
        setDetectedParams(null);
        setMappingResults(null);
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [inputText]);

  const performDetection = async (text) => {
    setIsDetecting(true);
    try {
      const resp = await fetch('/api/detect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ raw_text: text })
      });
      if (resp.ok) {
        const data = await resp.json();
        setDetectedParams(data);
        setManualSourceDb(data.source_db);
        setManualSeparator(data.separator);
        setManualSpecies(data.species);
        setOutputSeparator(data.separator_char === '\t' ? 'tab' : data.separator); // Default output sep to detected
      } else {
        setDetectedParams(null);
      }
    } catch (err) {
      console.error(err);
      setDetectedParams(null);
    } finally {
      setIsDetecting(false);
    }
  };

  const handleMap = async () => {
    if (!inputText.trim()) return;
    setIsMapping(true);
    try {
      const resp = await fetch('/api/map', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          raw_text: inputText,
          target_db: targetDb,
          fill_na: fillNa,
          separator: manualSeparator || detectedParams?.separator || ',',
          source_db: manualSourceDb || detectedParams?.source_db,
          species: manualSpecies || detectedParams?.species
        })
      });
      if (resp.ok) {
        const data = await resp.json();
        setMappingResults(data);
        setCurrentPage(1); // Reset page on new map
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsMapping(false);
    }
  };

  const downloadResults = () => {
    if (!mappingResults?.mapped_data) return;

    // Choose actual character for separator and file extension
    let sepChar = '\t';
    let extension = 'tsv';

    if (outputSeparator === 'comma') {
      sepChar = ',';
      extension = 'csv';
    } else if (outputSeparator === 'space') {
      sepChar = ' ';
      extension = 'txt';
    } else if (outputSeparator === 'newline') {
      sepChar = '\n';
      extension = 'txt';
    }

    // Create header
    let content = `Original ID (${mappingResults.source_db})${sepChar}Mapped ID (${mappingResults.target_db})\n`;

    processedData.forEach(row => {
      content += `${row.original}${sepChar}${row.mapped}\n`;
    });

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mapped_results_${mappingResults.target_db}.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyColumn = (colType) => {
    if (!processedData || !processedData.length) return;

    // colType is 'original' or 'mapped'
    const textData = processedData.map(row => row[colType]).join('\n');
    navigator.clipboard.writeText(textData)
      .then(() => alert(`Copied ${processedData.length} items to clipboard!`))
      .catch(err => console.error("Could not copy text: ", err));
  };

  // Data Processing Derivations
  const processedData = useMemo(() => {
    if (!mappingResults?.mapped_data) return [];
    if (viewMode === 'grouped') return mappingResults.mapped_data;

    // One by one mode
    const flattened = [];
    mappingResults.mapped_data.forEach(row => {
      if (row.mapped === fillNa) {
        flattened.push({ original: row.original, mapped: row.mapped });
      } else {
        const parts = row.mapped.split(';').map(p => p.trim()).filter(Boolean);
        if (parts.length === 0) {
          flattened.push({ original: row.original, mapped: fillNa });
        } else {
          parts.forEach(part => {
            flattened.push({ original: row.original, mapped: part });
          });
        }
      }
    });
    return flattened;
  }, [mappingResults, viewMode, fillNa]);

  const totalPages = processedData.length > 0 ? Math.ceil(processedData.length / ITEMS_PER_PAGE) : 0;
  const currentData = processedData.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-[1400px] min-h-screen">
      <header className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <img
              src="/msid-mapping.svg"
              alt="MSID Mapping Logo"
              className="h-10 md:h-12 w-auto"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <FileSearch className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-sm font-semibold">Input Detection</h3>
                <p className="text-xs text-muted-foreground">Auto-detects format & species</p>
              </div>
            </div>
          </div>
          <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <ArrowRightLeft className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-sm font-semibold">Smart Conversion</h3>
                <p className="text-xs text-muted-foreground">Map between 20+ databases</p>
              </div>
            </div>
          </div>
          <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Download className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-sm font-semibold">Export Results</h3>
                <p className="text-xs text-muted-foreground">Multiple format support</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        {/* Left Column - Input Box */}
        <div className="lg:col-span-4">
          <Card className="border-border border-2 p-0 gap-0 overflow-hidden h-full rounded-xl">
            <CardHeader className="bg-primary/5 border-b pt-3 pb-3 rounded-t-[10px]">
              <CardTitle className="flex items-center gap-2 text-base font-semibold">
                <FileSearch className="w-4 h-4 text-muted-foreground" />
                Input IDs
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 pb-4">
              <div className="flex flex-col gap-3">
                {detectedParams && !isDetecting && (
                  <div className="p-3 bg-primary/5 border border-primary/20 rounded-xl flex flex-col gap-2">
                    <h3 className="text-sm font-bold text-primary flex items-center gap-2 whitespace-nowrap">
                      <div className="h-2 w-2 rounded-full bg-primary"></div>
                      Auto-Detected
                    </h3>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="text-muted-foreground text-[10px]">Database:</span>
                      <Badge variant="outline" className="font-mono bg-background text-[10px] px-1.5 py-0">{detectedParams.source_db}</Badge>
                      <span className="text-muted-foreground text-[10px] ml-1">Species:</span>
                      <Badge variant="outline" className="font-mono bg-background text-[10px] px-1.5 py-0">{detectedParams.species}</Badge>
                      <span className="text-muted-foreground text-[10px] ml-1">Separator:</span>
                      <Badge variant="outline" className="font-mono bg-background text-[10px] px-1.5 py-0">{detectedParams.separator}</Badge>
                    </div>
                  </div>
                )}
                <Textarea
                  placeholder="Paste your IDs here...
e.g. ENSG00000141510, ENSG00000100906
or P04637_HUMAN, P53_HUMAN
or separate by tabs, spaces, newlines"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  className="min-h-[300px] font-mono resize-y border-border focus:border-primary focus:ring-1 focus:ring-primary rounded-xl placeholder:text-muted-foreground/30"
                />

                {isDetecting && (
                  <div className="flex items-center gap-2 text-primary text-sm font-medium">
                    <div className="h-2 w-2 rounded-full bg-primary animate-pulse"></div>
                    Analyzing input patterns...
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Middle Column - Converter Box */}
        <div className="lg:col-span-4">
          <Card className="border-border border-2 p-0 gap-0 overflow-hidden h-full rounded-xl">
            <CardHeader className="bg-primary/5 border-b pt-3 pb-3 rounded-t-[10px]">
              <CardTitle className="flex items-center gap-2 text-base font-semibold">
                <ArrowRightLeft className="w-4 h-4 text-muted-foreground" />
                Converter
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 pb-4">
              <div className="flex flex-col gap-4">
                {/* Manual Overrides Section */}
                <div className="space-y-3 p-4 bg-primary/5 border border-primary/20 rounded-xl">
                  <h3 className="text-sm font-bold text-primary flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-primary"></div>
                    Manual Overrides
                  </h3>

                  <div className="flex flex-row flex-wrap gap-3 w-full">
                    <div className="flex flex-col gap-1.5 w-[140px]">
                      <Label className="text-[11px] font-medium text-muted-foreground ml-1">Source Database</Label>
                      <Select value={manualSourceDb} onValueChange={setManualSourceDb}>
                        <SelectTrigger className="w-[140px] border-border h-7 text-[10px] px-1.5 truncate flex-shrink-0">
                          <SelectValue placeholder={detectedParams?.source_db || "Auto-detected"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Unknown">Unknown</SelectItem>
                          {TARGET_DB_OPTIONS.map(db => <SelectItem key={db} value={db}>{db}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex flex-col gap-1.5 w-[110px]">
                      <Label className="text-[11px] font-medium text-muted-foreground ml-1">Species</Label>
                      <Select value={manualSpecies} onValueChange={setManualSpecies}>
                        <SelectTrigger className="w-[110px] border-border h-7 text-[10px] px-1.5 truncate flex-shrink-0">
                          <SelectValue placeholder={detectedParams?.species || "Auto-detected"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Unknown">Unknown</SelectItem>
                          {availableSpecies.map(sp => (
                            <SelectItem key={sp} value={sp}>{sp}</SelectItem>
                          ))}
                          {manualSpecies && manualSpecies !== 'Unknown' && !availableSpecies.includes(manualSpecies) && (
                            <SelectItem value={manualSpecies}>{manualSpecies}</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex flex-col gap-1.5 w-[90px]">
                      <Label className="text-[11px] font-medium text-muted-foreground ml-1">Separator</Label>
                      <Select value={manualSeparator} onValueChange={setManualSeparator}>
                        <SelectTrigger className="w-[90px] border-border h-7 text-[10px] px-1.5 truncate flex-shrink-0">
                          <SelectValue placeholder={detectedParams?.separator || "Auto-detected"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="newline">New Line</SelectItem>
                          <SelectItem value="comma">Comma</SelectItem>
                          <SelectItem value="tab">Tab</SelectItem>
                          <SelectItem value="space">Space</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Target Configuration Section */}
                <div className="space-y-3 p-4 bg-primary/5 border border-primary/20 rounded-xl">
                  <h3 className="text-sm font-bold text-primary flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-primary"></div>
                    Target Configuration
                  </h3>

                  <div className="flex flex-row flex-wrap gap-3 w-full">
                    <div className="flex flex-col gap-1.5 w-[140px]">
                      <Label className="text-[11px] font-medium text-muted-foreground ml-1">Target Database</Label>
                      <Select value={targetDb} onValueChange={setTargetDb}>
                        <SelectTrigger className="w-[140px] border-border h-7 text-[10px] px-1.5 truncate flex-shrink-0">
                          <SelectValue placeholder="Select target database" />
                        </SelectTrigger>
                        <SelectContent>
                          {TARGET_DB_OPTIONS.map(db => (
                            <SelectItem key={db} value={db}>{db}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex flex-col gap-1.5 w-[110px]">
                      <Label className="text-[11px] font-medium text-muted-foreground ml-1">Missing Value</Label>
                      <Input
                        type="text"
                        value={fillNa}
                        onChange={(e) => setFillNa(e.target.value)}
                        placeholder="N/A"
                        className="w-[110px] border-border h-7 text-[10px] px-1.5 flex-shrink-0"
                      />
                    </div>
                  </div>
                </div>

                {/* Convert Button */}
                <div className="mt-4">
                  <Button
                    size="lg"
                    onClick={handleMap}
                    disabled={isMapping || !inputText.trim()}
                    className="w-full text-base font-bold rounded-full bg-primary hover:bg-primary/90 h-12"
                  >
                    {isMapping ? (
                      <>
                        <div className="h-4 w-4 rounded-full border-2 border-background border-t-transparent animate-spin mr-2"></div>
                        Converting...
                      </>
                    ) : (
                      <>
                        <ArrowRightLeft className="w-4 h-4 mr-2" />
                        Convert IDs
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Results */}
        <div className="lg:col-span-4">
          {mappingResults ? (
            <Card className="border-border border-2 p-0 gap-0 overflow-hidden h-full rounded-xl">
              <CardHeader className="flex flex-row items-center justify-between pb-3 pt-3 border-b bg-primary/5 rounded-t-[10px]">
                <div className="flex flex-col gap-1">
                  <CardTitle className="text-lg font-bold">Results</CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs font-mono bg-background">
                      {mappingResults.source_db}
                    </Badge>
                    <ArrowRightLeft className="w-3 h-3 text-muted-foreground" />
                    <Badge variant="outline" className="text-xs font-mono bg-background">
                      {mappingResults.target_db}
                    </Badge>
                  </div>
                </div>
                <Button variant="secondary" size="sm" onClick={downloadResults} className="rounded-full">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="flex flex-col sm:flex-row w-full gap-4 items-center justify-between mb-4">
                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs font-medium whitespace-nowrap">Output Separator</Label>
                      <Select value={outputSeparator} onValueChange={setOutputSeparator}>
                        <SelectTrigger className="w-32 h-8 border-border text-xs">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tab">Tab</SelectItem>
                          <SelectItem value="comma">Comma</SelectItem>
                          <SelectItem value="space">Space</SelectItem>
                          <SelectItem value="newline">New Line</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center gap-2">
                      <Label className="text-xs font-medium whitespace-nowrap">View Mode</Label>
                      <Select value={viewMode} onValueChange={(val) => { setViewMode(val); setCurrentPage(1); }}>
                        <SelectTrigger className="w-32 h-8 border-border text-xs">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="grouped">Grouped</SelectItem>
                          <SelectItem value="one-by-one">One by one</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-3 p-3 bg-muted/20 rounded-xl">
                    <div className="flex flex-col items-center justify-center p-2 bg-background rounded-lg">
                      <span className="text-xs text-muted-foreground">Total</span>
                      <span className="text-lg font-bold">{processedData.length}</span>
                    </div>
                    <div className="flex flex-col items-center justify-center p-2 bg-background rounded-lg">
                      <span className="text-xs text-muted-foreground">Mapped</span>
                      <span className="text-lg font-bold text-success">
                        {processedData.filter(row => row.mapped !== fillNa).length}
                      </span>
                    </div>
                  </div>

                  {/* Results Table */}
                  <div className="rounded-lg border border-border overflow-hidden">
                    <div className="max-h-[400px] overflow-y-auto">
                      <Table>
                        <TableHeader className="bg-muted/30 sticky top-0 z-10">
                          <TableRow className="border-border">
                            <TableHead className="py-2 px-4 whitespace-nowrap text-xs font-semibold text-foreground">
                              <div className="flex items-center justify-between">
                                {mappingResults.source_db}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 ml-2 text-muted-foreground hover:text-primary hover:bg-primary/10"
                                  onClick={() => copyColumn('original')}
                                  title="Copy Original IDs"
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                            </TableHead>
                            <TableHead className="py-2 px-4 whitespace-nowrap text-xs font-semibold text-foreground border-l border-border">
                              <div className="flex items-center justify-between">
                                {mappingResults.target_db}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 ml-2 text-muted-foreground hover:text-primary hover:bg-primary/10"
                                  onClick={() => copyColumn('mapped')}
                                  title="Copy Mapped IDs"
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {currentData.map((row, idx) => (
                            <TableRow key={idx} className="border-border hover:bg-muted/20">
                              <TableCell className="py-2 px-4 font-mono text-xs border-r border-border">
                                <div className="truncate max-w-[120px]" title={row.original}>
                                  {row.original}
                                </div>
                              </TableCell>
                              <TableCell className="py-2 px-4 font-mono text-xs">
                                <div className={`truncate max-w-[120px] ${row.mapped === fillNa ? 'text-warning font-semibold' : 'text-success'}`} title={row.mapped}>
                                  {row.mapped}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between p-3 bg-muted/10 rounded-xl border border-border">
                      <Button
                        variant="outline"
                        size="icon"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(prev => prev - 1)}
                        className="h-8 w-8 rounded-full"
                      >
                        <ChevronLeft className="h-3 w-3" />
                      </Button>
                      <div className="text-center">
                        <p className="text-sm font-medium">Page {currentPage} of {totalPages}</p>
                        <p className="text-xs text-muted-foreground">
                          Showing {Math.min(currentPage * ITEMS_PER_PAGE, processedData.length)} of {processedData.length}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="icon"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(prev => prev + 1)}
                        className="h-8 w-8 rounded-full"
                      >
                        <ChevronRight className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-border border-2 p-0 gap-0 overflow-hidden h-full rounded-xl">
              <CardHeader className="bg-primary/5 border-b pt-3 pb-3 rounded-t-[10px]">
                <CardTitle className="text-base font-semibold">Results</CardTitle>
              </CardHeader>
              <CardContent className="pt-4 pb-4">
                <div className="flex flex-col items-center justify-center text-center p-6 mt-4">
                  <div className="h-12 w-12 rounded-full bg-muted/30 flex items-center justify-center mb-4">
                    <ArrowRightLeft className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <h3 className="text-sm font-medium mb-2">No results yet</h3>
                  <p className="text-xs text-muted-foreground">
                    Enter IDs on the left, configure conversion in the middle, and results will appear here.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  )
}

export default App
